import { api } from '../../api.js';
import { AuthSnapshot } from '../../auth.js';
import { apiUrl } from '../../config.js';
import { escapeHtml, renderLayout } from '../../layout.js';
import { navigate } from '../../router.js';
import { activeTaskId } from '../../core/system-pages/index.js';
import { recognitionOriginalUploadModalHint, type RecognitionOriginalPolicy } from '../../core/recognitionOriginalRetention.js';
import * as XLSX from 'xlsx';

type CloudFile = {
    id: string;
    originalName: string;
};
type RecognitionJob = {
    id: string;
    fileId?: string;
    kind?: 'customs' | 'invoice';
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: number;
    stage?: string;
    detail: string;
    error: string;
    createdAt?: string;
    updatedAt?: string;
    snapshot?: Record<string, unknown>;
};
type QueueStatus = 'pending' | 'uploading' | 'processing' | 'success' | 'error';
type QueueItem = {
    id: string;
    file: File;
    status: QueueStatus;
    statusText: string;
    detail: string;
    progress: number;
    planId: string;
    jobId?: string;
    startedAt?: number;
    remainingSeconds?: number;
};
type BusinessState = {
    invoices: Record<string, unknown>[];
};
type InvoiceLedgerPlan = {
    id: string;
    name: string;
    ledgerType?: string;
    version?: number;
};
type InvoiceGroup = {
    key: string;
    items: Record<string, unknown>[];
    number: string;
    taxRate: string;
    date: string;
    completedAt: string;
    buyer: string;
    seller: string;
    buyerTaxId: string;
    sellerTaxId: string;
    invoiceType: string;
    content: string;
    untaxed: number;
    tax: number;
    total: number;
    status: 'pending' | 'reissue';
    expenseCategory: string;
};
type InvoiceSortKey = 'number' | 'party' | 'amount' | 'date' | 'type' | 'category';
type InvoiceOptionalColumnKey = 'taxId' | 'content' | 'date' | 'type' | 'category' | 'remark';
type InvoiceColumnVisibility = Record<InvoiceOptionalColumnKey, boolean>;
type ExpenseCategoryItem = {
    id: string;
    name: string;
    group: 'system' | 'custom' | 'uncategorized';
};

const SYSTEM_EXPENSE_CATEGORIES = ['差旅费', '餐饮费', '通讯费', '材料费', '日杂费', '交通费', '咨询费', '办公费', '服务费', '劳务费', '其它费用'];
const UNCATEGORIZED_EXPENSE = '未分类';

const INVOICE_MANDATORY_COLUMNS = [
    {key: 'index', label: '序号/编号'},
    {key: 'party', label: '购方/销方名称'},
    {key: 'amount', label: '开票/价税金额'},
] as const;

const INVOICE_OPTIONAL_COLUMNS: Array<{key: InvoiceOptionalColumnKey; label: string}> = [
    {key: 'taxId', label: '购方/销方税号'},
    {key: 'content', label: '开票内容'},
    {key: 'date', label: '开票/入库日期'},
    {key: 'type', label: '发票类型'},
    {key: 'category', label: '费用归属'},
    {key: 'remark', label: '操作'},
];

function defaultInvoiceColumnVisibility(): InvoiceColumnVisibility {
    return {
        taxId: true,
        content: true,
        date: true,
        type: true,
        category: true,
        remark: true,
    };
}

function invoiceColumnStorageKey(organizationId: string) {
    return `tax-assistant-invoice-columns:${organizationId}`;
}

function loadInvoiceColumnVisibility(organizationId: string): InvoiceColumnVisibility {
    try {
        const raw = window.localStorage.getItem(invoiceColumnStorageKey(organizationId));
        const parsed = raw ? JSON.parse(raw) as Partial<InvoiceColumnVisibility> : {};
        return {...defaultInvoiceColumnVisibility(), ...parsed};
    }
    catch {
        return defaultInvoiceColumnVisibility();
    }
}

function saveInvoiceColumnVisibility(organizationId: string, visibility: InvoiceColumnVisibility): void {
    window.localStorage.setItem(invoiceColumnStorageKey(organizationId), JSON.stringify(visibility));
}

function columnHiddenAttr(visibility: InvoiceColumnVisibility, key: InvoiceOptionalColumnKey): string {
    return visibility[key] ? '' : ' hidden';
}

function applyInvoiceColumnVisibility(root: HTMLElement, visibility: InvoiceColumnVisibility) {
    INVOICE_OPTIONAL_COLUMNS.forEach(({key}) => {
        const hidden = !visibility[key];
        root.querySelectorAll(`[data-invoice-col="${key}"]`).forEach(node => {
            (node as HTMLElement).hidden = hidden;
        });
    });
}

function expenseCategoryStorageKey(organizationId: string) {
    return `tax-assistant-expense-categories:${organizationId}`;
}

function loadCustomExpenseCategories(organizationId: string): string[] {
    try {
        const raw = window.localStorage.getItem(expenseCategoryStorageKey(organizationId));
        const parsed = raw ? JSON.parse(raw) as unknown : [];
        return Array.isArray(parsed) ? [...new Set(parsed.map(item => String(item).trim()).filter(Boolean))] : [];
    }
    catch {
        return [];
    }
}

function saveCustomExpenseCategories(organizationId: string, names: string[]): void {
    window.localStorage.setItem(expenseCategoryStorageKey(organizationId), JSON.stringify(names));
}

function listExpenseCategories(organizationId: string): ExpenseCategoryItem[] {
    const custom = loadCustomExpenseCategories(organizationId).filter(name => name !== UNCATEGORIZED_EXPENSE && !SYSTEM_EXPENSE_CATEGORIES.includes(name));
    return [
        ...SYSTEM_EXPENSE_CATEGORIES.map(name => ({id: `system:${name}`, name, group: 'system' as const})),
        ...custom.map(name => ({id: `custom:${name}`, name, group: 'custom' as const})),
        {id: 'uncategorized', name: UNCATEGORIZED_EXPENSE, group: 'uncategorized'},
    ];
}

const text = (record: Record<string, unknown>, keys: string[]) => keys.map(key => String(record[key] ?? '').trim()).find(Boolean) || '';
const amount = (record: Record<string, unknown>, keys: string[]) => Number(text(record, keys).replace(/,/g, '')) || 0;

/** 用于忽略页面重渲染后仍在执行的旧异步任务 */
let activeInvoiceWorkspaceToken = 0;

function resolveInvoiceExportPlanId(
    ledgerType: string | undefined,
    currentPlanId: string,
    invoicePlans: InvoiceLedgerPlan[],
): string {
    if (ledgerType === 'invoice-ledger')
        return currentPlanId || invoicePlans[0]?.id || '';
    return invoicePlans[0]?.id || '';
}

type InvoiceExportMode = 'list' | 'plan';

/** 按台账方案调用服务端生成发票台账文件 */
async function exportInvoiceLedgerByPlan(taskId: string, planId: string, invoiceNumbers: string[]) {
    const result = await api.post<{file: {id: string}}>(apiUrl(`/api/ledger/tasks/${encodeURIComponent(taskId)}/generate`), { planId, invoiceNumbers });
    const download = await api.get<{downloadUrl: string}>(apiUrl(`/api/files/${encodeURIComponent(result.file.id)}/download-url`));
    downloadFileFromUrl(download.downloadUrl);
}

/**
 * 发票台账任务导出方式选择弹窗。
 * 报关单及发票匹配任务不调用此弹窗，仍直接按列表导出。
 */
function showInvoiceExportModal(
    root: HTMLElement,
    options: {
        count: number;
        hasPlans: boolean;
        getPlanId: () => string;
        onConfirm: (mode: InvoiceExportMode, planId: string) => Promise<void>;
    },
) {
    root.querySelector('#invoiceExportModal')?.remove();
    root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="invoiceExportModal"><div class="modal invoice-export-modal"><div class="modal-header"><div><h3>导出发票台账</h3><p class="modal-subtitle">已选择 ${options.count} 张发票</p></div><button type="button" class="modal-close" data-close-export>×</button></div><div class="modal-body"><div class="invoice-export-options"><label class="invoice-export-option"><input type="radio" name="invoiceExportMode" value="list" checked><span class="invoice-export-option-copy"><b>按当前列表导出</b><small>按页面列表字段与筛选结果生成 Excel，不套用台账方案模板</small></span></label><label class="invoice-export-option"><input type="radio" name="invoiceExportMode" value="plan" ${options.hasPlans ? '' : 'disabled'}><span class="invoice-export-option-copy"><b>按台账方案导出</b><small>使用页面上方已选台账方案的字段映射与模板结构</small></span></label></div><div class="form-message" id="invoiceExportMessage"></div></div><div class="modal-footer"><button type="button" class="secondary-button" data-close-export>取消</button><button type="button" class="primary-button" id="confirmInvoiceExport">开始导出</button></div></div></div>`);
    const modal = root.querySelector<HTMLElement>('#invoiceExportModal');
    if (!modal)
        return;
    const message = modal.querySelector<HTMLElement>('#invoiceExportMessage');
    const confirm = modal.querySelector<HTMLButtonElement>('#confirmInvoiceExport');
    const modeInputs = [...modal.querySelectorAll<HTMLInputElement>('input[name="invoiceExportMode"]')];
    const close = () => modal.remove();
    modal.querySelectorAll('[data-close-export]').forEach(button => button.addEventListener('click', close));
    modal.addEventListener('click', event => {
        if (event.target === modal)
            close();
    });
    confirm?.addEventListener('click', async () => {
        const mode = modeInputs.find(input => input.checked)?.value === 'plan' ? 'plan' : 'list';
        const planId = mode === 'plan' ? options.getPlanId() : '';
        if (mode === 'plan' && !planId) {
            if (message)
                message.textContent = '请先在页面上方选择台账方案';
            return;
        }
        try {
            if (confirm)
                confirm.disabled = true;
            if (message)
                message.textContent = '正在导出，请稍候…';
            await options.onConfirm(mode, planId);
            close();
        }
        catch (error) {
            if (message)
                message.textContent = error instanceof Error ? error.message : String(error);
        }
        finally {
            if (confirm)
                confirm.disabled = false;
        }
    });
}


function setInvoicePageMessage(root: HTMLElement, text: string, isError = false) {
    root.querySelectorAll<HTMLElement>('#invoiceMessage, #invoicePageNotice').forEach(node => {
        node.textContent = text;
        node.hidden = !text;
        node.classList.toggle('is-error', isError);
    });
}

function downloadFileFromUrl(downloadUrl: string) {
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
}

type InvoiceExportColumn = {
    label: string;
    value: (group: InvoiceGroup, index: number) => string | number;
};

/** 按页面列表结构生成导出列，并尊重字段选择设置 */
function buildInvoiceExportColumns(visibility: InvoiceColumnVisibility): InvoiceExportColumn[] {
    const columns: InvoiceExportColumn[] = [
        {label: '序号', value: (_group, index) => index + 1},
        {label: '发票号码', value: group => group.number || '未识别'},
        {label: '购方名称', value: group => group.buyer || '—'},
        {label: '销方名称', value: group => group.seller || '—'},
    ];
    if (visibility.taxId) {
        columns.push(
            {label: '购方税号', value: group => group.buyerTaxId || '—'},
            {label: '销方税号', value: group => group.sellerTaxId || '—'},
        );
    }
    columns.push(
        {label: '价税合计', value: group => group.total || 0},
        {label: '不含税金额', value: group => group.untaxed || 0},
        {label: '税额', value: group => group.tax || 0},
    );
    if (visibility.content)
        columns.push({label: '开票内容', value: group => group.content || '—'});
    if (visibility.date)
        columns.push({label: '开票日期', value: group => formatDisplayDate(group.date)});
    if (visibility.type)
        columns.push({label: '发票类型', value: group => formatInvoiceType(group.invoiceType)});
    if (visibility.category)
        columns.push({label: '费用归属', value: group => group.expenseCategory || UNCATEGORIZED_EXPENSE});
    return columns;
}

function selectedInvoiceGroupsFromDom(root: HTMLElement, groups: InvoiceGroup[]): InvoiceGroup[] {
    return [...root.querySelectorAll<HTMLTableRowElement>('[data-invoice-row]')]
        .map(row => {
            const checkbox = row.querySelector<HTMLInputElement>('[data-invoice-select]');
            if (!checkbox?.checked)
                return null;
            const key = checkbox.dataset.invoiceSelect || '';
            return groups.find(group => group.key === key) || null;
        })
        .filter((group): group is InvoiceGroup => Boolean(group));
}

/** 报关单匹配任务：按当前列表所见导出 Excel，不依赖台账方案 */
function exportMatchingTaskInvoiceList(groups: InvoiceGroup[], visibility: InvoiceColumnVisibility, taskName: string) {
    const columns = buildInvoiceExportColumns(visibility);
    const header = columns.map(column => column.label);
    const rows = groups.map((group, index) => columns.map(column => column.value(group, index)));
    const sheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, '发票列表');
    const safeName = taskName.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').slice(0, 40) || '发票列表';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    XLSX.writeFile(workbook, `${safeName}_发票列表_${date}.xlsx`);
}

/**
 * 解析发票识别请求使用的台账方案 ID。
 * 报关单及发票匹配任务已在报关单识别时绑定方案，应传空值由服务端读取任务快照。
 */
function resolveInvoiceTemplatePlanId(root: HTMLElement, task: {
    ledgerType?: string;
    currentPlanId?: string;
}, preferredPlanId = ''): string {
    if (task.ledgerType === 'invoice-ledger') {
        return preferredPlanId
            || root.querySelector<HTMLSelectElement>('#invoicePageLedgerPlan')?.value
            || root.querySelector<HTMLSelectElement>('#invoiceLedgerPlan')?.value
            || task.currentPlanId
            || '';
    }
    return '';
}

/** 读取页面工具栏当前选中的发票台账方案 */
function readInvoicePagePlanId(root: HTMLElement): string {
    return root.querySelector<HTMLSelectElement>('#invoicePageLedgerPlan')?.value || '';
}

export async function renderInvoicePage(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
    const workspaceToken = ++activeInvoiceWorkspaceToken;
    const taskId = activeTaskId(auth);
    if (!taskId) {
        renderLayout(root, auth, '<div class="legacy-loading">请先选择任务。<button class="primary-button" id="chooseTask">前往任务管理</button></div>');
        root.querySelector('#chooseTask')?.addEventListener('click', () => navigate('/tax-assistant/tasks'));
        return;
    }
    renderLayout(root, auth, '<div class="legacy-module-page"><div class="legacy-loading">正在读取发票数据…</div></div>');
    try {
        const [task, state, plans, jobsPayload, filesPayload] = await Promise.all([
            api.get<{task: {name: string; companyName: string; ledgerType?: string; currentPlanId?: string}; recognitionOriginalPolicy: RecognitionOriginalPolicy}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}`)),
            api.get<{state: BusinessState}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/state`)),
            api.get<{plans: InvoiceLedgerPlan[]}>(apiUrl('/api/ledger/plans')),
            api.get<{jobs: RecognitionJob[]}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/recognition-jobs`)),
            api.get<{files: CloudFile[]}>(apiUrl(`/api/files?taskId=${encodeURIComponent(taskId)}`)),
        ]);
        renderWorkspace(
            root,
            auth,
            taskId,
            task.task,
            state.state,
            plans.plans.filter(plan => plan.ledgerType === 'invoice-ledger'),
            jobsPayload.jobs.filter(job => job.kind === 'invoice'),
            filesPayload.files,
            workspaceToken,
            task.recognitionOriginalPolicy,
        );
    }
    catch (error) {
        renderLayout(root, auth, `<div class="legacy-module-page"><div class="legacy-loading">${escapeHtml(error instanceof Error ? error.message : String(error))}</div></div>`);
    }
}

function renderWorkspace(
    root: HTMLElement,
    auth: AuthSnapshot,
    taskId: string,
    task: {name: string; companyName: string; ledgerType?: string; currentPlanId?: string},
    state: BusinessState,
    plans: InvoiceLedgerPlan[],
    invoiceJobs: RecognitionJob[] = [],
    cloudFiles: CloudFile[] = [],
    workspaceToken = 0,
    retentionPolicy: RecognitionOriginalPolicy = {retentionDays: 7},
) {
    const isWorkspaceActive = () => workspaceToken === activeInvoiceWorkspaceToken;
    const groups = groupInvoices(state.invoices);
    const sellers = [...new Set(groups.map(group => group.seller).filter(Boolean))];
    const fileNames = new Map(cloudFiles.map(file => [file.id, file.originalName]));
    const queue: QueueItem[] = invoiceJobs
        .filter(job => job.kind !== 'customs' && ['queued', 'processing'].includes(job.status))
        .map(job => ({
            id: `inv-restored-${job.id}`,
            file: new File([], fileNames.get(String(job.fileId ?? '')) || '正在识别的发票'),
            status: 'processing' as const,
            statusText: job.detail || '识别中',
            detail: job.detail || '',
            progress: Math.max(1, Number(job.progress) || 0),
            planId: String(job.snapshot?.templatePlanId ?? ''),
            jobId: job.id,
            startedAt: Date.now(),
            remainingSeconds: undefined,
        }));
    let sortKey: InvoiceSortKey = 'date';
    let sortDirection: 1 | -1 = -1;
    const columnVisibility = loadInvoiceColumnVisibility(auth.organization.id);
    const renderTableBody = () => {
        const tbody = root.querySelector<HTMLElement>('#invoiceGridBody');
        if (!tbody)
            return;
        const sortedGroups = sortInvoiceGroups(groups, sortKey, sortDirection);
        tbody.innerHTML = renderInvoiceGridRows(sortedGroups, columnVisibility);
        bindInvoiceRowActions(root, auth, taskId, sortedGroups, root.querySelector('#invoiceMessage'), renderTableBody);
        applyInvoiceColumnVisibility(root, columnVisibility);
    };
    const renderSortButtons = () => {
        root.querySelectorAll<HTMLButtonElement>('[data-invoice-sort]').forEach(button => {
            const key = button.dataset.invoiceSort as InvoiceSortKey;
            const active = sortKey === key;
            button.classList.toggle('is-active', active);
            button.classList.toggle('is-asc', active && sortDirection === 1);
            button.classList.toggle('is-desc', active && sortDirection === -1);
        });
    };
    const selectedPlanId = resolveInvoiceExportPlanId(task.ledgerType, task.currentPlanId || '', plans);
    const pagePlanSelectorHtml = task.ledgerType === 'invoice-ledger'
        ? `<label class="invoice-page-plan-field" id="invoicePagePlanField"><span>台账方案</span><select class="form-select" id="invoicePageLedgerPlan" ${plans.length ? '' : 'disabled'}><option value="">— 请选择方案 —</option>${plans.map(plan => `<option value="${escapeHtml(plan.id)}" ${plan.id === selectedPlanId ? 'selected' : ''}>${escapeHtml(plan.name)}</option>`).join('')}</select></label>`
        : '';
    renderLayout(root, auth, `<div class="legacy-module-page invoice-recognition-page"><section class="invoice-page-head"><div class="invoice-page-actions"><button type="button" class="primary-button" id="openInvoiceUploadModal">上传发票</button><button type="button" class="secondary-button invoice-batch-action-button is-hidden" id="assignExpenseCategory">批量选择费用归属</button><button type="button" class="secondary-button invoice-batch-action-button is-hidden" id="exportInvoiceLedger">导出发票台账</button><button type="button" class="invoice-delete-button" id="batchDeleteInvoices" disabled>删除所选 0</button></div><div class="invoice-page-tools">${pagePlanSelectorHtml}<button type="button" class="secondary-button" id="openInvoiceColumnModal">字段选择</button><select class="form-select" id="invSellerFilter"><option value="">全部厂家</option>${sellers.map(seller => `<option>${escapeHtml(seller)}</option>`).join('')}</select><select class="form-select" id="invCategoryFilter"><option value="">全部费用归属</option>${[...new Set(groups.map(group => group.expenseCategory))].map(category => `<option>${escapeHtml(category)}</option>`).join('')}</select><div class="invoice-search-group"><input class="form-input invoice-page-search" id="invSearch" type="search" placeholder="搜索发票号码、购销方"><button type="button" class="secondary-button" id="invSearchButton">搜索</button></div></div></section><div class="invoice-page-notice" id="invoicePageNotice" hidden></div><section class="invoice-list-panel"><div class="table-scroll"><table class="invoice-grid-table"><thead id="invoiceGridHead">${renderInvoiceTableHead(columnVisibility)}</thead><tbody id="invoiceGridBody">${renderInvoiceGridRows(sortInvoiceGroups(groups, sortKey, sortDirection), columnVisibility)}</tbody></table></div><div class="form-message" id="invoiceMessage"></div></section></div>`);
    const message = root.querySelector<HTMLElement>('#invoiceMessage');
    bindInvoiceActionsV2(root, auth, taskId, groups, message, task.ledgerType, task.name, task.currentPlanId, plans, columnVisibility, renderTableBody);
    renderSortButtons();
    root.querySelectorAll<HTMLButtonElement>('[data-invoice-sort]').forEach(button => {
        button.addEventListener('click', () => {
            const key = button.dataset.invoiceSort as InvoiceSortKey;
            if (sortKey === key)
                sortDirection = sortDirection === 1 ? -1 : 1;
            else {
                sortKey = key;
                sortDirection = key === 'number' || key === 'party' ? 1 : -1;
            }
            renderSortButtons();
            renderTableBody();
        });
    });
    const filterRows = () => {
        const seller = root.querySelector<HTMLSelectElement>('#invSellerFilter')?.value || '';
        const category = root.querySelector<HTMLSelectElement>('#invCategoryFilter')?.value || '';
        const keyword = root.querySelector<HTMLInputElement>('#invSearch')?.value.trim().toLowerCase() || '';
        root.querySelectorAll<HTMLTableRowElement>('[data-invoice-row]').forEach(row => {
            row.hidden = Boolean(
                (seller && row.dataset.seller !== seller)
                || (category && row.dataset.category !== category)
                || (keyword && !String(row.dataset.search || '').includes(keyword)),
            );
        });
    };
    root.querySelector('#invSellerFilter')?.addEventListener('change', filterRows);
    root.querySelector('#invCategoryFilter')?.addEventListener('change', filterRows);
    root.querySelector('#invSearchButton')?.addEventListener('click', filterRows);
    root.querySelector('#invSearch')?.addEventListener('keydown', event => {
        if ((event as KeyboardEvent).key === 'Enter') {
            event.preventDefault();
            filterRows();
        }
    });
    root.querySelector('#openInvoiceColumnModal')?.addEventListener('click', () => {
        showInvoiceColumnModal(root, auth.organization.id, columnVisibility, next => {
            Object.assign(columnVisibility, next);
            saveInvoiceColumnVisibility(auth.organization.id, columnVisibility);
            applyInvoiceColumnVisibility(root, columnVisibility);
        });
    });
    applyInvoiceColumnVisibility(root, columnVisibility);
    const openUploadModal = (autoStart = false) => {
        root.querySelector('#invoiceUploadModal')?.remove();
        root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="invoiceUploadModal"><div class="modal invoice-upload-modal"><div class="modal-header"><div><h3>上传发票识别</h3><p class="modal-subtitle">${escapeHtml(recognitionOriginalUploadModalHint(retentionPolicy))}</p></div><button type="button" class="modal-close" data-close-upload ${queue.some(item => item.status === 'uploading' || item.status === 'processing') ? 'disabled' : ''}>×</button></div><div class="modal-body"><div class="invoice-plan-row" id="invoicePlanRow" hidden><label>发票台账方案<select class="form-select" id="invoiceLedgerPlan" ${plans.length ? '' : 'disabled'}><option value="">— 请选择发票台账方案 —</option>${plans.map(plan => `<option value="${escapeHtml(plan.id)}" ${plan.id === selectedPlanId ? 'selected' : ''}>${escapeHtml(plan.name)}</option>`).join('')}</select></label></div><div class="invoice-upload-dropzone" id="invModalDrop"><div class="upload-bar-icon">▥</div><div class="upload-bar-text"><div class="upload-bar-title">拖拽或选择发票文件</div><div class="upload-bar-sub">可一次选择多个文件，确认后点击开始识别</div></div><button type="button" class="secondary-button" id="chooseInvoiceFiles">选择文件</button><input id="invFile" type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls" multiple hidden></div><div class="invoice-modal-queue" id="invModalQueue" hidden><div class="invoice-modal-queue-head"><strong>待识别文件</strong><span id="invQueueCount"></span><button type="button" class="secondary-button" id="invQueueClearButton">清空</button></div><div id="invFileList"></div></div><div class="batch-progress-box" id="invBatchProgress"><div class="recognition-progress-head"><span id="invBatchProgressText">等待开始识别</span><b id="invBatchProgressPercent">0%</b></div><div class="recognition-progress-track"><div class="recognition-progress-fill" id="invBatchProgressFill"></div></div></div><div class="form-message" id="invModalMessage"></div></div><div class="modal-footer"><button type="button" class="secondary-button" data-close-upload ${queue.some(item => item.status === 'uploading' || item.status === 'processing') ? 'disabled' : ''}>取消</button><button type="button" class="primary-button" id="invQueueStartButton">开始识别</button></div></div></div>`);
        const modal = root.querySelector<HTMLElement>('#invoiceUploadModal');
        if (!modal)
            return;
        const modalMessage = modal.querySelector<HTMLElement>('#invModalMessage');
        const input = modal.querySelector<HTMLInputElement>('#invFile');
        const panel = modal.querySelector<HTMLElement>('#invModalQueue');
        const list = modal.querySelector<HTMLElement>('#invFileList');
        const count = modal.querySelector<HTMLElement>('#invQueueCount');
        const drop = modal.querySelector<HTMLElement>('#invModalDrop');
        let recognitionBusy = queue.some(item => item.status === 'processing' || item.status === 'uploading');
        const closeModal = (force = false) => {
            if (!force && recognitionBusy)
                return;
            modal.remove();
        };
        const redraw = () => {
            if (panel)
                panel.hidden = !queue.length;
            if (count)
                count.textContent = queue.length ? ` ${queue.length} 个文件` : '';
            const processable = queue.filter(item => item.status === 'pending' || item.status === 'error');
            const start = modal.querySelector<HTMLButtonElement>('#invQueueStartButton');
            const clear = modal.querySelector<HTMLButtonElement>('#invQueueClearButton');
            const closeButtons = modal.querySelectorAll<HTMLButtonElement>('[data-close-upload]');
            if (start) {
                start.disabled = recognitionBusy || !processable.length;
                start.textContent = processable.length && processable.every(item => item.status === 'error') ? '重试失败项' : '开始识别';
            }
            if (clear)
                clear.disabled = recognitionBusy;
            closeButtons.forEach(button => { button.disabled = recognitionBusy; });
            drop?.classList.toggle('busy', recognitionBusy);
            if (list) {
                list.innerHTML = queue.map((item, index) => {
                    const remaining = item.status === 'success'
                        ? '识别完成'
                        : item.status === 'error'
                            ? (item.detail || '识别失败')
                            : item.remainingSeconds === undefined
                                ? ''
                                : `预计剩余 ${formatCountdown(item.remainingSeconds)}`;
                    return `<div class="file-item" data-queue-id="${escapeHtml(item.id)}"><div class="file-order">${index + 1}</div><div class="file-info"><div class="file-name">${escapeHtml(item.file.name)}</div><div class="recognition-progress ${item.status === 'pending' ? '' : 'show'}"><div class="recognition-progress-head"><span>${escapeHtml(item.statusText)}</span><b>${escapeHtml(remaining)}</b></div><div class="recognition-progress-track"><div class="recognition-progress-fill ${item.status === 'success' ? 'success' : item.status === 'error' ? 'error' : ''}" style="width:${item.progress}%"></div></div></div></div><div class="action-btns"><button title="移除" class="del" data-remove-queue="${index}" ${recognitionBusy || !['pending', 'error'].includes(item.status) ? 'disabled' : ''}><span class="fi">&#xE74D;</span></button></div></div>`;
                }).join('');
                list.querySelectorAll<HTMLButtonElement>('[data-remove-queue]').forEach(button => button.addEventListener('click', () => {
                    queue.splice(Number(button.dataset.removeQueue), 1);
                    redraw();
                }));
            }
            const active = queue.filter(item => item.status === 'uploading' || item.status === 'processing');
            if (active.length)
                setProgress(modal, Math.round(active.reduce((sum, item) => sum + item.progress, 0) / active.length), active[active.length - 1]?.statusText || '正在识别');
            else
                modal.querySelector('#invBatchProgress')?.classList.remove('show');
        };
        const addFiles = (files: File[]) => {
            if (recognitionBusy) {
                if (modalMessage)
                    modalMessage.textContent = '当前批次正在识别，请完成后再添加';
                return;
            }
            const planId = resolveInvoiceTemplatePlanId(modal, task);
            let invalid = 0;
            let duplicate = 0;
            for (const file of files) {
                if (!/\.(pdf|jpe?g|png|xlsx?|xls)$/i.test(file.name)) {
                    invalid += 1;
                    continue;
                }
                if (queue.some(item => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified)) {
                    duplicate += 1;
                    continue;
                }
                queue.push({
                    id: `inv-queue-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    file,
                    status: 'pending',
                    statusText: '待识别',
                    detail: '',
                    progress: 0,
                    planId,
                });
            }
            const warnings = [invalid ? `${invalid} 个格式不支持` : '', duplicate ? `${duplicate} 个重复文件` : ''].filter(Boolean);
            if (modalMessage)
                modalMessage.textContent = warnings.length ? `已跳过：${warnings.join('、')}` : '';
            redraw();
        };
        const choose = () => {
            if (recognitionBusy) {
                if (modalMessage)
                    modalMessage.textContent = '当前批次正在识别，请完成后再添加';
                return;
            }
            input?.click();
        };
        const runRecognition = async () => {
            const processable = queue.filter(item => item.status === 'pending' || item.status === 'error');
            if (!processable.length || recognitionBusy)
                return;
            recognitionBusy = true;
            redraw();
            const existingNumbers = new Set(groups.map(group => group.number).filter(Boolean));
            let successCount = 0;
            let failCount = 0;
            for (const item of processable) {
                if (!isWorkspaceActive())
                    return;
                try {
                    if (item.jobId) {
                        item.status = 'processing';
                        item.statusText = '识别中';
                        item.startedAt = item.startedAt || Date.now();
                        item.remainingSeconds = item.remainingSeconds ?? 60;
    redraw();
                        await pollInvoiceJob(modal, taskId, item, redraw);
                    }
                    else {
                        if (!item.file.size)
                            throw new Error('缺少原始发票文件，请重新上传后再识别');
                        item.status = 'uploading';
                        item.statusText = '正在上传';
                        item.detail = `${formatSize(item.file.size)} · 正在上传临时识别文件`;
                        item.progress = 3;
                        redraw();
                        const signed = await api.post<{file: CloudFile; uploadUrl: string}>(apiUrl('/api/files/upload-url'), {
                            taskId,
                            kind: 'invoice',
                            fileName: item.file.name,
                            sizeBytes: item.file.size,
                            mimeType: item.file.type || mimeFromName(item.file.name),
                        });
                        if (!isWorkspaceActive())
                            return;
                        await api.upload(apiUrl(`/api/files/${encodeURIComponent(signed.file.id)}/content`), item.file, item.file.type || mimeFromName(item.file.name));
                        item.progress = 10;
                        item.statusText = '等待识别';
                        redraw();
                        const started = await api.post<{job: RecognitionJob}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/recognition-jobs`), {
            fileId: signed.file.id,
            kind: 'invoice',
                            templatePlanId: resolveInvoiceTemplatePlanId(modal, task, item.planId),
                        });
                        item.jobId = started.job.id;
                        item.status = 'processing';
                        item.statusText = '识别中';
                        item.startedAt = Date.now();
                        item.remainingSeconds = 60;
                        await pollInvoiceJob(modal, taskId, item, redraw);
                    }
                    if (!isWorkspaceActive())
                        return;
        const latest = await api.get<{state: BusinessState}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/state`));
                    const jobId = item.jobId;
                    const newNumbers = [...new Set(latest.state.invoices
                        .filter(record => String(record.recognitionJobId ?? '') === jobId)
                        .map(record => text(record, ['发票号码', 'invoiceNumber']))
                        .filter(Boolean))];
                    const duplicates = newNumbers.filter(number => existingNumbers.has(number));
                    if (duplicates.length && jobId && !window.confirm(`检测到发票号码 ${duplicates.join('、')} 已存在，是否仍然保留本次识别结果？`)) {
                        await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/recognition-jobs/batch-delete`), { jobIds: [jobId] });
                    }
                    else {
                        newNumbers.forEach(number => existingNumbers.add(number));
                    }
                    item.status = 'success';
                    item.statusText = '识别完成';
                    item.progress = 100;
                    item.remainingSeconds = 0;
                    successCount += 1;
                }
                catch (error) {
                    item.status = 'error';
                    item.statusText = '识别失败';
                    item.progress = 100;
                    item.detail = error instanceof Error ? error.message : String(error);
                    failCount += 1;
                }
                redraw();
            }
            if (!isWorkspaceActive())
                return;
            recognitionBusy = false;
            redraw();
            if (modalMessage) {
                if (successCount && !failCount)
                    modalMessage.textContent = `全部发票识别完成，共 ${successCount} 份`;
                else
                    modalMessage.textContent = `本批处理完成：成功 ${successCount} 份，失败 ${failCount} 份`;
            }
            if (successCount) {
                try {
                    await renderInvoicePage(root, auth);
                }
                catch {
                    redraw();
                }
            }
        };
        modal.querySelector('#chooseInvoiceFiles')?.addEventListener('click', choose);
        modal.querySelector('#invQueueClearButton')?.addEventListener('click', () => {
            if (recognitionBusy)
                return;
            queue.splice(0, queue.length);
            redraw();
            if (modalMessage)
                modalMessage.textContent = '';
        });
        input?.addEventListener('change', () => {
            addFiles(Array.from(input.files ?? []));
            input.value = '';
        });
        modal.querySelector('#invQueueStartButton')?.addEventListener('click', () => { void runRecognition(); });
        drop?.addEventListener('dragover', event => {
            event.preventDefault();
            if (!recognitionBusy)
                drop.classList.add('drag');
        });
        drop?.addEventListener('dragleave', () => drop.classList.remove('drag'));
        drop?.addEventListener('drop', event => {
            event.preventDefault();
            drop.classList.remove('drag');
            addFiles(Array.from(event.dataTransfer?.files ?? []));
        });
        modal.querySelectorAll('[data-close-upload]').forEach(button => button.addEventListener('click', () => closeModal()));
        modal.addEventListener('click', event => {
            if (event.target === modal && !recognitionBusy)
                closeModal();
        });
        redraw();
        if (autoStart || queue.some(item => item.jobId && (item.status === 'processing' || item.status === 'uploading'))) {
            recognitionBusy = true;
            redraw();
            void Promise.all(queue.filter(item => item.jobId).map(async item => {
                try {
                    await pollInvoiceJob(modal, taskId, item, redraw);
                    if (!isWorkspaceActive())
                        return;
                    item.status = 'success';
                    item.statusText = '识别完成';
                    item.progress = 100;
                    item.remainingSeconds = 0;
                }
                catch (error) {
                    if (!isWorkspaceActive())
                        return;
                    item.status = 'error';
                    item.statusText = '识别失败';
                    item.progress = 100;
                    item.detail = error instanceof Error ? error.message : String(error);
                }
                redraw();
            })).then(async () => {
                if (!isWorkspaceActive())
                    return;
                recognitionBusy = false;
                const successCount = queue.filter(item => item.status === 'success').length;
                const failCount = queue.filter(item => item.status === 'error').length;
                for (let index = queue.length - 1; index >= 0; index -= 1) {
                    if (queue[index]?.status === 'success')
                        queue.splice(index, 1);
                }
                redraw();
                if (modalMessage && failCount)
                    modalMessage.textContent = `恢复识别完成：成功 ${successCount} 份，失败 ${failCount} 份`;
                if (successCount) {
                    try {
                        await renderInvoicePage(root, auth);
                    }
                    catch {
                        redraw();
                    }
                }
            });
        }
    };
    root.querySelector('#openInvoiceUploadModal')?.addEventListener('click', () => openUploadModal());
    if (queue.length)
        openUploadModal(true);
}

async function pollInvoiceJob(container: HTMLElement, taskId: string, item: QueueItem, redraw: () => void) {
    if (!item.jobId)
        throw new Error('识别任务未创建');
    for (let attempt = 0; attempt < 360; attempt += 1) {
        const payload = await api.get<{job: RecognitionJob}>(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/recognition-jobs/${encodeURIComponent(item.jobId)}`));
        item.progress = Math.max(10, payload.job.progress);
        item.statusText = payload.job.detail || '正在识别';
        item.detail = payload.job.detail || '正在识别';
        if (payload.job.status === 'completed') {
            item.progress = 100;
            item.remainingSeconds = 0;
            redraw();
            return;
        }
        if (payload.job.status === 'failed')
            throw new Error(payload.job.error || payload.job.detail || '识别失败');
        const elapsed = Math.max(1, (Date.now() - (item.startedAt || Date.now())) / 1000);
        const progressed = Math.max(1, item.progress - 10);
        item.remainingSeconds = Math.min(1800, Math.max(1, Math.ceil(elapsed * (100 - item.progress) / progressed)));
        redraw();
        await new Promise(resolve => setTimeout(resolve, 2000));
    }
    throw new Error('识别等待超时，请稍后刷新查看');
}

function bindInvoiceActionsV2(
    root: HTMLElement,
    auth: AuthSnapshot,
    taskId: string,
    groups: InvoiceGroup[],
    message: HTMLElement | null,
    ledgerType: string | undefined,
    taskName: string,
    currentPlanId = '',
    invoicePlans: InvoiceLedgerPlan[] = [],
    columnVisibility: InvoiceColumnVisibility = defaultInvoiceColumnVisibility(),
    refreshRows?: () => void,
) {
    const selectAll = root.querySelector<HTMLInputElement>('#invoiceSelectAll');
    const batch = root.querySelector<HTMLButtonElement>('#batchDeleteInvoices');
    const assign = root.querySelector<HTMLButtonElement>('#assignExpenseCategory');
    const exportLedger = root.querySelector<HTMLButtonElement>('#exportInvoiceLedger');
    const selected = () => [...root.querySelectorAll<HTMLInputElement>('[data-invoice-select]:checked')];
    const update = () => {
        const inputs = [...root.querySelectorAll<HTMLInputElement>('[data-invoice-select]')];
        const count = selected().length;
        if (batch) {
            batch.disabled = !count;
            batch.textContent = `删除所选 ${count}`;
        }
        if (assign)
            assign.classList.toggle('is-hidden', !count);
        if (exportLedger)
            exportLedger.classList.toggle('is-hidden', !count);
        if (selectAll) {
            selectAll.checked = Boolean(inputs.length && inputs.every(input => input.checked));
            selectAll.indeterminate = Boolean(count && count < inputs.length);
        }
    };
    root.addEventListener('change', event => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-invoice-select]'))
            update();
    });
    selectAll?.addEventListener('change', () => {
        root.querySelectorAll<HTMLInputElement>('[data-invoice-select]').forEach(input => input.checked = Boolean(selectAll.checked));
        update();
    });
    root.querySelector<HTMLButtonElement>('#exportInvoiceLedger')?.addEventListener('click', async (event) => {
        const button = event.currentTarget as HTMLButtonElement;
        const selectedGroups = selectedInvoiceGroupsFromDom(root, groups);
        if (!selectedGroups.length) {
            const errorText = '请先勾选需要导出的发票';
            setInvoicePageMessage(root, errorText, true);
            window.alert(errorText);
            return;
        }
        /** 报关单及发票匹配任务：保持原行为，直接按列表导出 */
        if (ledgerType !== 'invoice-ledger') {
            try {
                button.disabled = true;
                button.textContent = '正在导出…';
                setInvoicePageMessage(root, `正在导出 ${selectedGroups.length} 张发票，请稍候…`);
                exportMatchingTaskInvoiceList(selectedGroups, columnVisibility, taskName);
                setInvoicePageMessage(root, `已导出 ${selectedGroups.length} 张发票列表。`);
            }
            catch (error) {
                const errorText = error instanceof Error ? error.message : String(error);
                setInvoicePageMessage(root, errorText, true);
                window.alert(errorText);
            }
            finally {
                button.disabled = false;
                button.textContent = '导出发票台账';
            }
            return;
        }
        showInvoiceExportModal(root, {
            count: selectedGroups.length,
            hasPlans: invoicePlans.length > 0,
            getPlanId: () => readInvoicePagePlanId(root),
            onConfirm: async (mode, planId) => {
        try {
            button.disabled = true;
                    button.textContent = '正在导出…';
                    setInvoicePageMessage(root, `正在导出 ${selectedGroups.length} 张发票，请稍候…`);
                    if (mode === 'list') {
                        exportMatchingTaskInvoiceList(selectedGroups, columnVisibility, taskName);
                        setInvoicePageMessage(root, `已按当前列表导出 ${selectedGroups.length} 张发票。`);
                        return;
                    }
                    const invoiceNumbers = selectedGroups.map(group => group.number || group.key).filter(Boolean);
                    await exportInvoiceLedgerByPlan(taskId, planId, invoiceNumbers);
                    setInvoicePageMessage(root, '发票台账已导出，正在打开下载。');
                }
                finally {
            button.disabled = false;
                    button.textContent = '导出发票台账';
        }
            },
        });
    });
    batch?.addEventListener('click', async () => {
        const numbers = selected().map(input => input.dataset.invoiceSelect || '').filter(Boolean);
        if (!numbers.length || !confirm(`确定删除所选 ${numbers.length} 张发票吗？`))
            return;
        try {
            await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/invoices/batch-delete`), { numbers });
            await renderInvoicePage(root, auth);
        }
        catch (error) {
            if (message)
                message.textContent = error instanceof Error ? error.message : String(error);
        }
    });
    assign?.addEventListener('click', () => {
        const keys = selected().map(input => input.dataset.invoiceSelect || '').filter(Boolean);
        if (!keys.length) {
            if (message)
                message.textContent = '请先勾选需要设置费用归属的发票';
            return;
        }
        showExpenseCategoryModal(root, auth, taskId, groups, keys, message);
    });
    bindInvoiceRowActions(root, auth, taskId, groups, message, refreshRows);
    update();
}

function bindInvoiceRowActions(
    root: HTMLElement,
    auth: AuthSnapshot,
    taskId: string,
    groups: InvoiceGroup[],
    message: HTMLElement | null,
    refreshRows?: () => void,
) {
    root.querySelectorAll<HTMLButtonElement>('[data-view-key]').forEach(button => button.addEventListener('click', () => {
        const group = groups.find(item => item.key === button.dataset.viewKey);
        if (group)
            showDetails(root, group);
    }));
    root.querySelectorAll<HTMLButtonElement>('[data-category-key]').forEach(button => button.addEventListener('click', () => {
        const key = button.dataset.categoryKey || '';
        if (key)
            showExpenseCategoryModal(root, auth, taskId, groups, [key], message);
    }));
}

function groupInvoices(records: Record<string, unknown>[]): InvoiceGroup[] {
    const map = new Map<string, Record<string, unknown>[]>();
    records.forEach((record, index) => {
        const key = text(record, ['发票号码', 'invoiceNumber', 'sourceFileName', 'sourceFileId']) || `未识别发票-${index + 1}`;
        map.set(key, [...(map.get(key) || []), record]);
    });
    return [...map.entries()].map(([key, items]) => {
        const first = items[0];
        const untaxed = items.reduce((sum, item) => sum + amount(item, ['不含税金额', '金额']), 0) || amount(first, ['不含税金额合计']);
        const tax = items.reduce((sum, item) => sum + amount(item, ['税额']), 0) || amount(first, ['税额合计']);
        const content = [...new Set(items.map(item => text(item, ['发票商品名称', '商品名称', '货物或应税劳务名称'])).filter(Boolean))].join(' · ');
        return {
            key,
            items,
            number: text(first, ['发票号码', 'invoiceNumber']),
            taxRate: text(first, ['税率']),
            date: text(first, ['开票日期']),
            completedAt: text(first, ['recognitionCompletedAt']),
            buyer: text(first, ['购方名称', '购买方名称']),
            seller: text(first, ['销售方名称', '销方名称', '供应商', '厂家']),
            buyerTaxId: text(first, ['购方税号', '购买方纳税人识别号']),
            sellerTaxId: text(first, ['销方税号', '销售方纳税人识别号']),
            invoiceType: text(first, ['发票类型']),
            content,
            untaxed,
            tax,
            total: amount(first, ['价税合计']) || untaxed + tax,
            status: items.some(item => text(item, ['reviewStatus']) === 'reissue') ? 'reissue' : 'pending',
            expenseCategory: text(first, ['费用归属', 'expenseCategory']) || UNCATEGORIZED_EXPENSE,
        };
    });
}

function sortInvoiceGroups(groups: InvoiceGroup[], sortKey: InvoiceSortKey, direction: 1 | -1): InvoiceGroup[] {
    const sorted = [...groups].sort((left, right) => {
        const compareText = (a: string, b: string) => a.localeCompare(b, 'zh-CN');
        let result = 0;
        if (sortKey === 'number')
            result = compareText(left.number || left.key, right.number || right.key);
        else if (sortKey === 'party')
            result = compareText(left.buyer || left.seller, right.buyer || right.seller);
        else if (sortKey === 'amount')
            result = left.total - right.total;
        else if (sortKey === 'date')
            result = compareText(left.date, right.date);
        else if (sortKey === 'type')
            result = compareText(left.invoiceType, right.invoiceType);
        else if (sortKey === 'category')
            result = compareText(left.expenseCategory, right.expenseCategory);
        return result * direction;
    });
    return sorted;
}

function renderSortLabel(key: InvoiceSortKey, label: string): string {
    return `<button type="button" class="invoice-sort-button" data-invoice-sort="${key}" aria-label="按${label}排序"><span class="invoice-sort-label">${label}</span><span class="invoice-sort-icon" aria-hidden="true"></span></button>`;
}

function renderInvoiceTableHead(visibility: InvoiceColumnVisibility): string {
    return `<tr><th class="col-select"><input type="checkbox" id="invoiceSelectAll" aria-label="全选"></th><th class="col-index" data-invoice-col="index">${renderSortLabel('number', '发票号码')}</th><th class="col-party" data-invoice-col="party">${renderSortLabel('party', '购方/销方名称')}</th><th class="col-tax-id" data-invoice-col="taxId"${columnHiddenAttr(visibility, 'taxId')}>购方/销方税号</th><th class="col-amount" data-invoice-col="amount">${renderSortLabel('amount', '价税合计')}</th><th class="col-content" data-invoice-col="content"${columnHiddenAttr(visibility, 'content')}>开票内容</th><th class="col-date" data-invoice-col="date"${columnHiddenAttr(visibility, 'date')}>${renderSortLabel('date', '开票日期')}</th><th class="col-type" data-invoice-col="type"${columnHiddenAttr(visibility, 'type')}>${renderSortLabel('type', '发票类型')}</th><th class="col-category" data-invoice-col="category"${columnHiddenAttr(visibility, 'category')}>${renderSortLabel('category', '费用归属')}</th><th class="col-remark" data-invoice-col="remark"${columnHiddenAttr(visibility, 'remark')}>操作</th></tr>`;
}

function renderInvoiceGridRows(groups: InvoiceGroup[], visibility: InvoiceColumnVisibility): string {
    if (!groups.length)
        return '<tr><td colspan="10" class="legacy-empty-cell">暂无发票数据，点击“上传发票”开始识别</td></tr>';
    return groups.map((group, index) => {
        const search = `${group.number} ${group.buyer} ${group.seller} ${group.content} ${group.expenseCategory}`.toLowerCase();
        return `<tr data-invoice-row data-seller="${escapeHtml(group.seller)}" data-status="${group.status}" data-category="${escapeHtml(group.expenseCategory)}" data-search="${escapeHtml(search)}"><td class="col-select"><input type="checkbox" data-invoice-select="${escapeHtml(group.key)}"></td><td class="col-index" data-invoice-col="index"><div class="invoice-index-cell"><span class="invoice-index-no">${index + 1}</span><span class="invoice-index-code" title="发票号码（识别自发票票面）">${escapeHtml(group.number || '未识别')}</span></div></td><td class="col-party" data-invoice-col="party"><div class="invoice-party-cell"><span class="invoice-stacked-main">${escapeHtml(group.buyer || '—')}</span><span class="invoice-stacked-main is-secondary">${escapeHtml(group.seller || '—')}</span></div></td><td class="col-tax-id" data-invoice-col="taxId"${columnHiddenAttr(visibility, 'taxId')}><div class="invoice-tax-cell"><span class="invoice-stacked-main invoice-tax-id">${escapeHtml(group.buyerTaxId || '—')}</span><span class="invoice-stacked-sub">${escapeHtml(group.sellerTaxId || '—')}</span></div></td><td class="col-amount" data-invoice-col="amount"><div class="invoice-stacked"><span class="invoice-stacked-main invoice-amount-total">${escapeHtml(formatCurrency(group.total))}</span><span class="invoice-stacked-sub">${escapeHtml(`${formatCurrency(group.untaxed)} / ${formatCurrency(group.tax)}`)}</span></div></td><td class="col-content" data-invoice-col="content"${columnHiddenAttr(visibility, 'content')}><span class="invoice-content-text">${escapeHtml(group.content || '—')}</span></td><td class="col-date" data-invoice-col="date"${columnHiddenAttr(visibility, 'date')}>${escapeHtml(formatDisplayDate(group.date))}</td><td class="col-type" data-invoice-col="type"${columnHiddenAttr(visibility, 'type')}>${escapeHtml(formatInvoiceType(group.invoiceType))}</td><td class="col-category" data-invoice-col="category"${columnHiddenAttr(visibility, 'category')}><button type="button" class="invoice-category-button" data-category-key="${escapeHtml(group.key)}">${escapeHtml(group.expenseCategory || UNCATEGORIZED_EXPENSE)}</button></td><td class="col-remark" data-invoice-col="remark"${columnHiddenAttr(visibility, 'remark')}><button type="button" class="invoice-view-link" data-view-key="${escapeHtml(group.key)}">查看</button></td></tr>`;
    }).join('');
}

function showInvoiceColumnModal(
    root: HTMLElement,
    organizationId: string,
    visibility: InvoiceColumnVisibility,
    onConfirm: (next: InvoiceColumnVisibility) => void,
) {
    const draft = {...visibility};
    root.querySelector('#invoiceColumnModal')?.remove();
    const mandatoryRows = INVOICE_MANDATORY_COLUMNS.map(column => `<tr class="is-mandatory"><td>${escapeHtml(column.label)}</td><td><input type="checkbox" checked disabled aria-label="${escapeHtml(column.label)}"></td></tr>`).join('');
    const optionalRows = INVOICE_OPTIONAL_COLUMNS.map(column => `<tr><td>${escapeHtml(column.label)}</td><td><input type="checkbox" data-column-toggle="${column.key}" ${draft[column.key] ? 'checked' : ''} aria-label="${escapeHtml(column.label)}"></td></tr>`).join('');
    root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="invoiceColumnModal"><div class="modal invoice-column-modal"><div class="modal-header"><h3>字段选择</h3><button type="button" class="modal-close" data-close-columns>×</button></div><div class="modal-body invoice-column-modal-body"><aside class="invoice-column-nav"><button type="button" class="invoice-column-nav-item is-active" disabled>列表显示项目</button><button type="button" class="invoice-column-nav-item" disabled>开票公司检测</button><button type="button" class="invoice-column-nav-item" disabled>允许入库条件</button></aside><div class="invoice-column-panel"><div class="invoice-column-table-wrap"><table class="invoice-column-table"><thead><tr><th>列表项</th><th>是否显示</th></tr></thead><tbody>${mandatoryRows}${optionalRows}</tbody></table></div></div></div><div class="modal-footer"><button type="button" class="secondary-button" data-close-columns>取消</button><button type="button" class="primary-button" id="confirmInvoiceColumns">确定</button></div></div></div>`);
    const modal = root.querySelector<HTMLElement>('#invoiceColumnModal');
    if (!modal)
        return;
    const close = () => modal.remove();
    modal.querySelectorAll('[data-close-columns]').forEach(button => button.addEventListener('click', close));
    modal.addEventListener('click', event => {
        if (event.target === modal)
            close();
    });
    modal.querySelector('#confirmInvoiceColumns')?.addEventListener('click', () => {
        INVOICE_OPTIONAL_COLUMNS.forEach(({key}) => {
            const input = modal.querySelector<HTMLInputElement>(`[data-column-toggle="${key}"]`);
            draft[key] = Boolean(input?.checked);
        });
        onConfirm({...draft});
        close();
    });
}

function showExpenseCategoryModal(
    root: HTMLElement,
    auth: AuthSnapshot,
    taskId: string,
    groups: InvoiceGroup[],
    invoiceKeys: string[],
    message: HTMLElement | null,
) {
    const organizationId = auth.organization.id;
    const current = groups.find(group => invoiceKeys.includes(group.key))?.expenseCategory || UNCATEGORIZED_EXPENSE;
    let selectedName = current;
    root.querySelector('#invoiceCategoryModal')?.remove();
    root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="invoiceCategoryModal"><div class="modal invoice-category-modal"><div class="modal-header"><h3>选择发票分类：</h3><div class="invoice-category-head-actions"><button type="button" class="invoice-category-add" id="addExpenseCategory">+ 添加一级分类</button><button type="button" class="modal-close" data-close-category>×</button></div></div><div class="modal-body"><div class="invoice-category-table-wrap"><table class="invoice-category-table"><thead><tr><th>分类名称</th><th>发票数量</th><th>操作</th></tr></thead><tbody id="invoiceCategoryBody"></tbody></table></div></div><div class="invoice-category-selected">已选择：<span class="invoice-category-pill" id="invoiceCategorySelected">${escapeHtml(selectedName)}</span></div><div class="modal-footer"><button type="button" class="secondary-button" data-close-category>取消</button><button type="button" class="primary-button" id="confirmExpenseCategory">确定</button></div></div></div>`);
    const modal = root.querySelector<HTMLElement>('#invoiceCategoryModal');
    if (!modal)
        return;
    const renderList = () => {
        const body = modal.querySelector('#invoiceCategoryBody');
        if (!body)
            return;
        const counts = new Map<string, number>();
        groups.forEach(group => counts.set(group.expenseCategory || UNCATEGORIZED_EXPENSE, (counts.get(group.expenseCategory || UNCATEGORIZED_EXPENSE) || 0) + 1));
        const items = listExpenseCategories(organizationId);
        const systemItems = items.filter(item => item.group === 'system');
        const customItems = items.filter(item => item.group === 'custom');
        const uncategorized = items.find(item => item.group === 'uncategorized');
        const row = (item: ExpenseCategoryItem, indent = false, removable = false) => {
            const checked = selectedName === item.name ? 'checked' : '';
            return `<tr class="${indent ? 'is-child' : ''}"><td><label class="invoice-category-name"><input type="radio" name="invoiceExpenseCategory" value="${escapeHtml(item.name)}" ${checked}><span>${escapeHtml(item.name)}</span></label></td><td>${counts.get(item.name) || 0}</td><td>${removable ? `<button type="button" class="invoice-category-remove" data-remove-category="${escapeHtml(item.name)}" aria-label="删除分类">×</button>` : ''}</td></tr>`;
        };
        body.innerHTML = [
            `<tr class="is-group"><td colspan="3"><span class="invoice-category-folder">系统默认</span></td></tr>`,
            ...systemItems.map(item => row(item, true)),
            `<tr class="is-group"><td colspan="3"><span class="invoice-category-folder">自定义</span></td></tr>`,
            ...(customItems.length ? customItems.map(item => row(item, true, true)) : ['<tr class="is-child"><td colspan="3" class="invoice-category-empty">暂无自定义分类</td></tr>']),
            uncategorized ? row(uncategorized) : '',
        ].join('');
        const selectedLabel = modal.querySelector('#invoiceCategorySelected');
        if (selectedLabel)
            selectedLabel.textContent = selectedName;
        body.querySelectorAll<HTMLInputElement>('input[name="invoiceExpenseCategory"]').forEach(input => input.addEventListener('change', () => {
            selectedName = input.value;
            if (selectedLabel)
                selectedLabel.textContent = selectedName;
        }));
        body.querySelectorAll<HTMLButtonElement>('[data-remove-category]').forEach(button => button.addEventListener('click', () => {
            const name = button.dataset.removeCategory || '';
            if (!name || !window.confirm(`确定删除分类“${name}”吗？`))
                return;
            saveCustomExpenseCategories(organizationId, loadCustomExpenseCategories(organizationId).filter(item => item !== name));
            if (selectedName === name)
                selectedName = UNCATEGORIZED_EXPENSE;
            renderList();
        }));
    };
    modal.querySelector('#addExpenseCategory')?.addEventListener('click', () => {
        const name = window.prompt('请输入一级分类名称')?.trim() || '';
        if (!name)
            return;
        if (listExpenseCategories(organizationId).some(item => item.name === name)) {
            window.alert('该分类已存在');
            return;
        }
        saveCustomExpenseCategories(organizationId, [...loadCustomExpenseCategories(organizationId), name]);
        selectedName = name;
        renderList();
    });
    modal.querySelector('#confirmExpenseCategory')?.addEventListener('click', async () => {
        const button = modal.querySelector<HTMLButtonElement>('#confirmExpenseCategory');
        if (button)
            button.disabled = true;
        try {
            await api.post(apiUrl(`/api/tasks/${encodeURIComponent(taskId)}/invoices/expense-category`), {
                invoiceKeys,
                category: selectedName,
            });
            modal.remove();
            await renderInvoicePage(root, auth);
        }
        catch (error) {
            if (message)
                message.textContent = error instanceof Error ? error.message : String(error);
            if (button)
                button.disabled = false;
        }
    });
    modal.querySelectorAll('[data-close-category]').forEach(button => button.addEventListener('click', () => modal.remove()));
    modal.addEventListener('click', event => {
        if (event.target === modal)
            modal.remove();
    });
    renderList();
}

function showDetails(root: HTMLElement, group: InvoiceGroup) {
    root.querySelector('#invoiceDetailModal')?.remove();
    root.insertAdjacentHTML('beforeend', `<div class="modal-overlay show" id="invoiceDetailModal"><div class="modal legacy-detail-modal"><div class="modal-header"><h3>发票明细</h3><button class="modal-close" data-close>×</button></div><div class="modal-body"><div class="legacy-detail-summary"><span>不含税金额 <b>${group.untaxed.toFixed(2)}</b></span><span>税额 <b>${group.tax.toFixed(2)}</b></span><span>价税合计 <b>${group.total.toFixed(2)}</b></span></div><div class="table-scroll"><table><thead><tr><th>行号</th><th>商品名称</th><th>规格型号</th><th>数量</th><th>单位</th><th>金额</th><th>税额</th></tr></thead><tbody>${group.items.map((item, index) => `<tr><td>${escapeHtml(text(item, ['发票行号', '行号']) || String(index + 1))}</td><td>${escapeHtml(text(item, ['发票商品名称', '商品名称']) || '-')}</td><td>${escapeHtml(text(item, ['规格型号', '规格']) || '-')}</td><td>${escapeHtml(text(item, ['发票数量', '数量']) || '-')}</td><td>${escapeHtml(text(item, ['发票单位', '单位']) || '-')}</td><td>${amount(item, ['不含税金额', '金额']).toFixed(2)}</td><td>${amount(item, ['税额']).toFixed(2)}</td></tr>`).join('')}</tbody></table></div><p class="manual-match-hint">线上版仅保留识别后的结构化数据，发票原件识别成功后已删除。</p></div><div class="modal-footer"><button class="primary-button" data-close>关闭</button></div></div></div>`);
    const modal = root.querySelector<HTMLElement>('#invoiceDetailModal');
    modal?.querySelectorAll('[data-close]').forEach(button => button.addEventListener('click', () => modal.remove()));
}

function setProgress(container: HTMLElement, value: number, label: string) {
    container.querySelector('#invBatchProgress')?.classList.add('show');
    const textNode = container.querySelector<HTMLElement>('#invBatchProgressText');
    const percent = container.querySelector<HTMLElement>('#invBatchProgressPercent');
    const fill = container.querySelector<HTMLElement>('#invBatchProgressFill');
    if (textNode)
        textNode.textContent = label;
    if (percent)
        percent.textContent = `${value}%`;
    if (fill)
        fill.style.width = `${value}%`;
}

function formatCountdown(seconds: number) {
    const value = Math.max(0, Math.round(seconds));
    if (value < 60)
        return `${value} 秒`;
    const minutes = Math.floor(value / 60);
    const remainder = value % 60;
    return `${minutes} 分 ${String(remainder).padStart(2, '0')} 秒`;
}

function formatCurrency(value: number) {
    if (!value)
        return '—';
    return `¥ ${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDisplayDate(value: string) {
    if (!value)
        return '—';
    const normalized = value.replace(/\//g, '-');
    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime()))
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    return value;
}

/** 展示发票实质类型，隐藏“电子发票”等载体描述 */
function formatInvoiceType(value: string) {
    if (!value)
        return '—';
    const normalized = value
        .replace(/^电子发票[\s（(]*/i, '')
        .replace(/[）)]\s*$/g, '')
        .trim();
    return normalized || '—';
}

function mimeFromName(name: string) {
    const lower = name.toLowerCase();
    if (lower.endsWith('.pdf'))
        return 'application/pdf';
    if (lower.endsWith('.png'))
        return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg'))
        return 'image/jpeg';
    if (lower.endsWith('.xls'))
        return 'application/vnd.ms-excel';
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

function formatSize(value: number) {
    if (value < 1024)
        return `${value} B`;
    if (value < 1024 * 1024)
        return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
}
